import { useMemo } from 'react';

import type { InquiryGroupConversationRoute } from '@/components/chat/InquiryGroupConversations';
import { useAnonymousInquiryGroupingEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import type { GetInquiryV2Response } from '@/services/getInquiryV2';
import {
  getInquiryDisplayParticipant,
  isAnonymousPrimumRoutedInquiry,
} from '@/util/inquiryDisplay';
import {
  getGroupTabLabel,
  getRelayViewTabTitle,
  getVisibleInquiryGroupTabInquiries,
} from '@/util/inquiryGroups';
import type { QuestionGroupChatTab } from '@/util/questionGroups';
import { getModRouteLabel, resolveModGroupRoutes } from '@/utils/modHelpers';

type Inquiry = NonNullable<GetInquiryV2Response>;

/**
 * Tab routes for switching between sibling inquiries in a group. The fullscreen
 * chat route does not need the inbox list mounted — each route is one
 * conversation id plus a display title.
 *
 * Title resolution:
 * - **MOD Direct-to-MSL** (route set on inquiry.title): label by route
 *   (J&J MSL vs J&J Medical Information Team) — both sibling inquiries
 *   point to the same MOD expert user, so we'd get duplicate names otherwise.
 * - **Otherwise**: label by the other participant's name (existing behavior
 *   for LMP→multi-expert grouped inquiries).
 *
 * Only the inquiry author gets routes. Recipients see no grouped tabs.
 */
export function useInquiryGroupTabs({
  enabled,
  userId,
  inquiry,
  questionGroupTabs,
}: {
  enabled: boolean;
  userId?: string;
  inquiry?: Inquiry;
  /** The inquiry group card's tabs, which replace the send's own when the chat was opened from one. */
  questionGroupTabs?: QuestionGroupChatTab[];
}) {
  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  return useMemo<InquiryGroupConversationRoute[]>(() => {
    if (questionGroupTabs) {
      return questionGroupTabs.map((tab) => ({
        key: tab.thread.conversationId,
        conversationId: tab.thread.conversationId,
        title: tab.label,
        labels: tab.thread.labels,
      }));
    }

    if (!enabled || !userId || !inquiry) {
      return [];
    }

    const groupInquiries = inquiry.groupInquiries ?? [];

    if (groupInquiries.length <= 1) {
      return [];
    }

    const visibleInquiries = getVisibleInquiryGroupTabInquiries(
      groupInquiries,
      'chat-group-tabs',
      userId,
      isAnonymousInquiryGroupingEnabled
    );

    const intUserId = Number(userId);

    const isAskAnonymouslyRelayView =
      isAnonymousPrimumRoutedInquiry(inquiry) && inquiry.recipient.id === intUserId;

    const assignedRoutes = resolveModGroupRoutes(visibleInquiries);

    return visibleInquiries.map((relatedInquiry) => {
      const route = assignedRoutes.get(relatedInquiry.conversationId);

      if (route) {
        return {
          key: relatedInquiry.conversationId,
          conversationId: relatedInquiry.conversationId,
          title: getModRouteLabel(route),
          labels: relatedInquiry.labels,
        };
      }

      const relatedFallbackParticipant =
        relatedInquiry.author.id === intUserId ? relatedInquiry.recipient : relatedInquiry.author;

      const otherParticipant = getInquiryDisplayParticipant(
        relatedInquiry,
        relatedFallbackParticipant,
        isAnonymousInquiryGroupingEnabled
      );

      const tabParticipantLabel = getGroupTabLabel({
        firstName: otherParticipant.firstName,
        lastName: otherParticipant.lastName,
        identifiesAsDoctor: otherParticipant.identifiesAsDoctor,
      });

      return {
        key: relatedInquiry.conversationId,
        conversationId: relatedInquiry.conversationId,
        title: isAskAnonymouslyRelayView
          ? getRelayViewTabTitle(relatedInquiry.relayedTo, tabParticipantLabel)
          : tabParticipantLabel,
      };
    });
  }, [enabled, userId, inquiry, isAnonymousInquiryGroupingEnabled, questionGroupTabs]);
}
